import { memo, useCallback, useRef, useState } from 'react';
import { Popover, PopoverTrigger, PopoverSurface, Textarea, Button, mergeClasses } from '@fluentui/react-components';
import { CommentRegular, CommentFilled } from '@fluentui/react-icons';
import type { OpenPopoverEvents, OnOpenChangeData } from '@fluentui/react-components';
import { useTimeGridStyles } from '../useTimeGridStyles';

type CommentPopoverProps = {
    comment?: string;
    onSave: (comment: string) => void;
};

export const CommentPopover = memo(function CommentPopover({ comment, onSave }: CommentPopoverProps) {
    const [open, setOpen] = useState(false);
    const [localComment, setLocalComment] = useState(comment ?? '');
    const initialCommentRef = useRef(comment ?? '');
    const styles = useTimeGridStyles();

    const hasComment = Boolean(comment);

    const handleOpenChange = useCallback(
        (_e: OpenPopoverEvents, data: OnOpenChangeData) => {
            if (data.open) {
                // Sync local state when opening
                setLocalComment(comment ?? '');
                initialCommentRef.current = comment ?? '';
            } else {
                // Save on close if changed
                const trimmed = localComment.trim();
                if (trimmed !== initialCommentRef.current) {
                    onSave(trimmed);
                }
            }
            setOpen(data.open);
        },
        [comment, localComment, onSave],
    );

    const handleTextareaChange = useCallback((_e: React.ChangeEvent<HTMLTextAreaElement>, data: { value: string }) => {
        setLocalComment(data.value);
    }, []);

    return (
        <Popover open={open} onOpenChange={handleOpenChange} positioning="below-end" trapFocus>
            <PopoverTrigger disableButtonEnhancement>
                <Button
                    className={mergeClasses('fui-trigger-comment', styles.commentTrigger, hasComment && styles.commentTriggerHasComment)}
                    appearance="subtle"
                    size="small"
                    icon={hasComment ? <CommentFilled /> : <CommentRegular />}
                    aria-label="Add comment"
                    tabIndex={-1}
                />
            </PopoverTrigger>
            <PopoverSurface>
                <Textarea className={styles.commentTextarea} value={localComment} onChange={handleTextareaChange} placeholder="Add a comment..." autoFocus />
            </PopoverSurface>
        </Popover>
    );
});
